import { ReactNode, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import FilterModal from './FilterModal';
import SortModal from './SortModal';

import { useFormControl, useToggleV2, useWatch } from 'hooks';

import { getFirst } from 'utils/array';
import { appStorage } from 'utils/storage';
import { generateOptions } from './utils';

import { ChildrenProps, FilterOption, ListFilters, ListSorters, ManipulationState } from './types';

interface ListManipulationProps<Entity> {
  itens: Entity[];
  filters?: ListFilters<Entity>[];
  sorters?: ListSorters<Entity>[];
  storageKey?: string;

  children: (props: ChildrenProps<Entity>) => ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadState(key?: string, sorters?: ListSorters<any>[]): ManipulationState {
  const basic = {
    filters: {},
    sorters: (sorters ?? []).filter((s) => s.default).map(({ id }) => id),
  };

  if (!key) return basic;

  return {
    ...basic,
    ...appStorage.durable.get(key),
  };
}

export function ListManipulation<Entity>({
  itens,
  filters,
  sorters,
  children,
  storageKey,
}: ListManipulationProps<Entity>) {
  const { t } = useLang();

  const [filterOn, filterHandlers] = useToggleV2();
  const [sortOn, sortHandlers] = useToggleV2();

  const { dispatch, state } = useFormControl<ManipulationState>(loadState(storageKey, sorters));

  useWatch(state, (newState) => storageKey && appStorage.durable.set(storageKey, newState));

  useWatch(itens, () => {
    if (!itens.length) return;

    dispatch({
      filters: ({ filters: activeFilters }) => {
        return Object.fromEntries(
          Object.entries(activeFilters || {})
            .map(([key, value]) => {
              if (!value.length) return null;

              const config = (filters || []).find(({ property, id }) => (id ?? property) === key);

              if (!config) return [key, value];

              const { multiple } = config;

              const previouslyMultiple = Array.isArray(value);

              if (previouslyMultiple !== multiple) return null;

              const current = [value].flat();

              const options = generateOptions({ t, itens, ...config });

              const stillValid = current.filter((selectedValue) =>
                options.find((option) => option.value === selectedValue),
              );

              if (!stillValid.length) return null;

              return [key, multiple ? stillValid : getFirst(stillValid)];
            })
            .filter(Boolean) as Array<[string, string | string[]]>,
        );
      },
    });
  });

  const parsedItens = useMemo(() => {
    const filtered = filters
      ? itens.filter((item) => {
          const matches = Object.entries(state.filters).every(([key, value]) => {
            if (!value.length) return true;

            const config = filters.find(({ property, id }) => (id ?? property) === key);

            if (!config) return true;

            const { resolver, property, optionExtractor } = config;

            const options = [optionExtractor?.(item, t)].flat().filter(Boolean) as FilterOption[];
            const selectedList = [value].flat();

            return (
              resolver?.(item, value) ??
              options.some(({ value }) => selectedList.includes(value)) ??
              selectedList.includes(`${item[property]}`)
            );
          });

          return matches;
        })
      : itens;

    const sorted = sorters
      ? filtered.slice().sort((a, b) => {
          return state.sorters.reduce((acc, sortRule) => {
            if (acc !== 0) return acc;

            const config = sorters.find(({ id }) => id === sortRule);

            return config?.sorter(a, b) ?? acc;
          }, 0);
        })
      : filtered;

    return sorted;
  }, [itens, filters, state, t, sorters]);

  const hasFilter = useMemo(() => {
    return !!filters?.some(
      ({ property, id }) => !!state.filters[id ?? (property as string)]?.length,
    );
  }, [filters, state]);

  const hasSorter = useMemo(() => {
    return !!sorters?.filter((s) => !s.default).some(({ id }) => !!state.sorters.includes(id));
  }, [sorters, state]);

  return (
    <>
      {filters && (
        <FilterModal
          isOpen={filterOn}
          close={filterHandlers.turnOff}
          dispatch={dispatch}
          activeFilters={state.filters}
          filters={filters}
          itens={itens}
          hasFilter={hasFilter}
        />
      )}

      {sorters && (
        <SortModal
          isOpen={sortOn}
          close={sortHandlers.turnOff}
          dispatch={dispatch}
          activeSorters={state.sorters}
          sorters={sorters}
          itens={itens}
        />
      )}

      {children({
        parsedItens,
        openFilters: filterHandlers.turnOn,
        hasFilter,
        hasSorter,
        openSorters: sortHandlers.turnOn,
        activeFilters: state.filters,
      })}
    </>
  );
}

export type { ListFilters, ListSorters, FilterOption, Filters } from './types';
